import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RefsProvider } from './context/RefsContext';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { TicketsListPage } from './pages/TicketsListPage';
import { TicketDetailPage } from './pages/TicketDetailPage';
import { NewTicketPage } from './pages/NewTicketPage';
import { RechargeCreditPage } from './pages/RechargeCreditPage';
import { AdminCongesPage } from './pages/AdminCongesPage';
import { StatsClientsPage } from './pages/StatsClientsPage';
import { StatsInternesPage } from './pages/StatsInternesPage';
import { AdminClientsPage } from './pages/AdminClientsPage';
import { AdminClientEditPage } from './pages/AdminClientEditPage';
import { AdminOnly } from './components/AdminOnly';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { VaultCreatePage } from './pages/VaultCreatePage';
import { VaultViewPage } from './pages/VaultViewPage';
import { ProfilePage } from './pages/ProfilePage';
import { AideConnexionPage } from './pages/AideConnexionPage';
import { AppShell } from './components/AppShell';
import { PreprodBanner } from './components/PreprodBanner';
import { loginRedirectState } from './lib/loginRedirect';
import './index.css';

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading, unavailable, sessionExpired, refresh } = useAuth();
  const loc = useLocation();
  if (loading) return <div style={{ padding: 24, color: 'var(--text-mute)' }}>Chargement…</div>;
  // Profil illisible pour une raison passagère (429, 5xx, réseau) : la session
  // n'est pas perdue, on ne renvoie pas vers /login. Nouvel essai automatique.
  if (!user && unavailable) {
    return (
      <div role="status" style={{ maxWidth: 480, margin: '64px auto', padding: '0 16px', textAlign: 'center' }}>
        <div
          style={{
            color: 'var(--text)',
            fontSize: 'var(--fs-base)',
            padding: 14,
            background: 'var(--warning-soft)',
            border: '1px solid #fcd34d',
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {unavailable}
        </div>
        <div style={{ color: 'var(--text-mute)', fontSize: 'var(--fs-xs)', marginBottom: 12 }}>
          Nouvel essai automatique dans quelques secondes.
        </div>
        <button type="button" className="btn" onClick={() => void refresh()}>
          Réessayer maintenant
        </button>
      </div>
    );
  }
  // Pas de session, ou session expirée en cours de route : page de connexion,
  // avec l'adresse demandée pour y revenir ensuite.
  if (!user) return <Navigate to="/login" state={loginRedirectState(loc, sessionExpired)} replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RefsProvider>
          <PreprodBanner />
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot" element={<ForgotPasswordPage />} />
          <Route path="/reset/:token" element={<ResetPasswordPage />} />
          <Route path="/s/:token" element={<VaultViewPage />} />
          {/* Documentation publique, lisible sans session (hors de la garde). */}
          <Route path="/aide/connexion" element={<AideConnexionPage />} />
          <Route path="/aide" element={<Navigate to="/aide/connexion" replace />} />
          <Route
            element={
              <Protected>
                <AppShell />
              </Protected>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="tickets" element={<TicketsListPage />} />
            <Route path="tickets/new" element={<NewTicketPage />} />
            <Route path="tickets/:id" element={<TicketDetailPage />} />
            <Route path="vault/new" element={<VaultCreatePage />} />
            <Route path="me" element={<ProfilePage />} />
            <Route path="credit/recharge" element={<RechargeCreditPage />} />
            <Route path="admin/conges" element={<AdminOnly><AdminCongesPage /></AdminOnly>} />
            <Route path="stats" element={<AdminOnly><StatsClientsPage /></AdminOnly>} />
            <Route path="stats/temps" element={<AdminOnly><StatsInternesPage /></AdminOnly>} />
            <Route path="admin/clients" element={<AdminOnly><AdminClientsPage /></AdminOnly>} />
            <Route path="admin/clients/new" element={<AdminOnly><AdminClientEditPage /></AdminOnly>} />
            <Route path="admin/clients/:id" element={<AdminOnly><AdminClientEditPage /></AdminOnly>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        </RefsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
